'use client';
// frontend/src/app/contact/page.tsx
import { useRef, useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useTranslations } from 'next-intl';

export default function ContactPage() {
  const captchaRef = useRef<HCaptcha>(null);
  const hcaptchaSiteKey = process.env.NEXT_PUBLIC_HCAPTCHA_SITE_KEY;
  const t = useTranslations('contact');

  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [message,      setMessage]      = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [status,       setStatus]       = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg,       setErrMsg]       = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');

    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    if (!apiUrl) {
      setErrMsg('API not configured.');
      setStatus('error');
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, captchaToken }),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setErrMsg((json as { error?: string }).error ?? `Error ${res.status}`);
        setStatus('error');
        captchaRef.current?.resetCaptcha();
        setCaptchaToken('');
        return;
      }

      setStatus('sent');
    } catch {
      setErrMsg('Network error. Please try again.');
      setStatus('error');
      captchaRef.current?.resetCaptcha();
      setCaptchaToken('');
    }
  }

  if (status === 'sent') {
    return (
      <main className="max-w-xl mx-auto px-4 py-16 text-center flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-[#e8f5e9] flex items-center justify-center text-[#2d7a4f] text-2xl">
          ✓
        </div>
        <h1 className="text-2xl font-bold text-[#1a2e1f]">{t('title')}</h1>
        <p className="text-[#6b8c7a] text-sm">
          {t('success')}
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-4 py-12 flex flex-col gap-8">
      <div>
        <h1 className="text-3xl font-bold text-[#1a2e1f]">{t('title')}</h1>
        <p className="mt-2 text-[#6b8c7a] text-sm">
          {t('subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <label htmlFor="name" className="text-sm font-medium text-[#1a2e1f]">
            {t('name')} <span className="text-[#9db8a5] font-normal">({t('optional')})</span>
          </label>
          <input
            id="name"
            type="text"
            maxLength={100}
            value={name}
            onChange={e => setName(e.target.value)}
            className="border border-[#cdd8cc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]"
            placeholder="Jane Smith"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="email" className="text-sm font-medium text-[#1a2e1f]">{t('email')}</label>
          <input
            id="email"
            type="email"
            required
            maxLength={254}
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="border border-[#cdd8cc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d7a4f]"
            placeholder="jane@example.com"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="message" className="text-sm font-medium text-[#1a2e1f]">{t('message')}</label>
          <textarea
            id="message"
            required
            minLength={10}
            maxLength={5000}
            rows={6}
            value={message}
            onChange={e => setMessage(e.target.value)}
            className="border border-[#cdd8cc] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2d7a4f] resize-y"
            placeholder="Tell us what's on your mind..."
          />
          <p className="text-xs text-[#9db8a5] text-right">{message.length}/5000</p>
        </div>

        {status === 'error' && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {errMsg}
          </p>
        )}

        <div className="flex flex-col gap-1">
          {hcaptchaSiteKey ? (
            <HCaptcha
              ref={captchaRef}
              sitekey={hcaptchaSiteKey}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken('')}
            />
          ) : (
            <p className="text-sm text-red-600">hCaptcha site key not configured.</p>
          )}
        </div>

        <button
          type="submit"
          disabled={status === 'sending' || !captchaToken}
          title={!captchaToken ? 'Complete the captcha above to continue' : undefined}
          className="bg-[#2d7a4f] text-white rounded-xl px-6 py-3 text-sm font-semibold hover:bg-[#235f3d] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? t('sending') : t('send')}
        </button>
      </form>
    </main>
  );
}
